import {
  CSSProperties,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  forwardRef,
  useMemo,
} from "react";

import { InlineMessage, LoadingSpinner } from "chameleon/lib/components";

import {
  applePayVariantButtonStyles,
  buttonClass,
  buttonWrapper,
  criticalButtonClass,
  iconVariantButtonStyles,
  outlineVariantButtonStyles,
  textVariantButtonStyles,
} from "./button.css";

export type ButtonProps = {
  /**
   * ID used for accessibility
   */
  id: string;
  /**
   * Is the Button disabled?
   */
  isDisabled?: boolean;
  /**
   * Is the button loading?
   */
  isLoading?: boolean;
  /**
   * onclick handler
   */
  onClick?: (e?: MouseEvent) => void | unknown;
  /**
   * onKeyDown handler
   */
  onKeyDown?: (e?: KeyboardEvent) => void | unknown;
  /**
   * Text to display in the button
   */
  text: string;
  /**
   * Tab index for the input
   */
  tabIndex?: number;
  /**
   * Icon in button
   */
  svg?: ReactElement;
  /**
   * Position of the icon
   */
  position?: "left" | "right";
  /**
   * Test id will be applied if added
   */
  testId?: string;
  /**
   *    * Variant - default | text
   */
  variant?: "default" | "outline" | "text" | "icon" | "applePay" | "critical";
  /**
   * Error message for the button
   */
  errorMessage?: string;
  /**
   * Error Message Key for caption test id
   */
  errorMessageKey?: string;
  /**
   * Flag for hiding messaging in iframe wrapper
   */
  inIframe?: boolean;
  /**
   * Additional styles
   */
  style?: CSSProperties;
  /**
   * aria label text
   */
  ariaLabel?: string;
};

/**
 * A basic button with a loading state
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      id,
      isDisabled = false,
      isLoading = false,
      text,
      onClick,
      onKeyDown,
      tabIndex,
      svg,
      testId,
      position = "left",
      variant = "default",
      errorMessage,
      errorMessageKey,
      inIframe = false,
      style,
      ariaLabel,
    }: ButtonProps,
    ref
  ) => {
    const variantStyles = useMemo(() => {
      switch (variant) {
        case "text":
          return textVariantButtonStyles;
        case "outline":
          return outlineVariantButtonStyles;
        case "icon":
          return iconVariantButtonStyles;
        case "applePay":
          return applePayVariantButtonStyles;
        case "critical":
          return criticalButtonClass;
        default:
          return "";
      }
    }, [variant]);

    const captionID = `${id}-${errorMessageKey}-caption`;

    return (
      <div className={buttonWrapper}>
        <button
          aria-label={ariaLabel}
          disabled={isDisabled}
          onClick={onClick}
          onKeyDown={onKeyDown}
          className={`${buttonClass} ${variantStyles}`}
          tabIndex={tabIndex}
          ref={ref}
          data-testid={testId}
          style={style}
        >
          <>
            {svg && position == "left" ? svg : undefined}
            {isLoading && <LoadingSpinner sizeVariant={24} />}
            {variant !== "icon" && text}
            {svg && position == "right" ? svg : undefined}
          </>
        </button>
        {errorMessage && !inIframe && (
          <InlineMessage id={captionID} message={errorMessage} type={"error"} />
        )}
      </div>
    );
  }
);

Button.displayName = "Button";
